import { isArray } from 'lodash';
import {
  PlaceTypeEnum,
  SearchEntityTagOperatorEnum,
  SearchUserEntityTagInput,
  UserSearchEntityTagNameEnum,
} from '@src/graphql/generated';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { MAX_SALARY, MIN_SALARY } from '@src/containers/Source/constants/salaryRange';
import { GetSearchTagsValue } from './interfaces/getSearchTags.interface';

export const getSearchTags = (queryParams: QueryParams): GetSearchTagsValue => {
  const {
    searchPatterns = [],
    place,
    isRelocation,
    isRemote,
    salaryRange = [MIN_SALARY.toString(), MAX_SALARY.toString()],
    seniorities = [],
    specialities = [],
    talents = [],
    joinedFrom,
    joinedTo,
  } = queryParams;

  const parsedSalaryRange = salaryRange.map(salary => parseFloat(salary));

  const getUserSearchEntityTagNameEnumByPlaceType = (
    placeType: PlaceTypeEnum,
  ): UserSearchEntityTagNameEnum | undefined => {
    if (placeType === PlaceTypeEnum.COUNTRY) {
      return UserSearchEntityTagNameEnum.COUNTRY;
    }

    if (placeType === PlaceTypeEnum.LOCATION) {
      return UserSearchEntityTagNameEnum.LOCATION;
    }

    if (placeType === PlaceTypeEnum.RELOCATION) {
      return UserSearchEntityTagNameEnum.RELOCATION;
    }

    if (placeType === PlaceTypeEnum.REMOTE) {
      return UserSearchEntityTagNameEnum.REMOTE;
    }
  };

  const searchPatternSearchTags: SearchUserEntityTagInput[] = searchPatterns.map(item => ({
    operator: SearchEntityTagOperatorEnum.SEARCH_PHRASE,
    tag: UserSearchEntityTagNameEnum.NAME,
    value: item.pattern,
  }));

  let placeSearchTags: SearchUserEntityTagInput[] = [];

  if (!place?.type) {
    placeSearchTags = [];
  } else {
    const placeType = getUserSearchEntityTagNameEnumByPlaceType(place.type);

    placeSearchTags = placeType
      ? [
          {
            tag: placeType,
            value: place.id,
          },
        ]
      : [];
  }

  let relocationSearchTags: SearchUserEntityTagInput[] = [];

  if (!isRelocation) {
    relocationSearchTags = [];
  } else {
    relocationSearchTags = [
      {
        tag: UserSearchEntityTagNameEnum.RELOCATION,
        value: 'true',
      },
    ];
  }

  let remoteSearchTags: SearchUserEntityTagInput[] = [];

  if (!isRemote) {
    remoteSearchTags = [];
  } else {
    remoteSearchTags = [
      {
        tag: UserSearchEntityTagNameEnum.REMOTE,
        value: 'true',
      },
    ];
  }

  const specialitySearchTags: SearchUserEntityTagInput[] = specialities.map(({ id }) => ({
    tag: UserSearchEntityTagNameEnum.SPECIALITY,
    value: id,
  }));

  const senioritySearchTags: SearchUserEntityTagInput[] = seniorities.map(({ id }) => ({
    tag: UserSearchEntityTagNameEnum.SENIORITY,
    value: id,
  }));

  let salaryRangeSearchTags: SearchUserEntityTagInput[] = [];

  if (parsedSalaryRange[1] === MAX_SALARY) {
    salaryRangeSearchTags = [
      {
        operator: SearchEntityTagOperatorEnum.GT,
        tag: UserSearchEntityTagNameEnum.SALARY_MONTHLY_EUR,
        value: parsedSalaryRange[0],
      },
    ];
  }

  salaryRangeSearchTags = [
    {
      operator: SearchEntityTagOperatorEnum.GT,
      tag: UserSearchEntityTagNameEnum.SALARY_MONTHLY_EUR,
      value: parsedSalaryRange[0],
    },
    {
      operator: SearchEntityTagOperatorEnum.LT,
      tag: UserSearchEntityTagNameEnum.SALARY_MONTHLY_EUR,
      value: parsedSalaryRange[1],
    },
  ];

  let talentsSearchTags: SearchUserEntityTagInput[] = [];

  if (isArray(talents)) {
    talentsSearchTags = talents.map(id => ({
      tag: UserSearchEntityTagNameEnum.ID,
      value: id,
    }));
  } else if (typeof talents === 'object') {
    talentsSearchTags = Object.values(talents).map(id => ({
      tag: UserSearchEntityTagNameEnum.ID,
      value: id,
    }));
  }

  const verificationPublishedAtSearchTags: SearchUserEntityTagInput[] = [];

  if (joinedFrom) {
    verificationPublishedAtSearchTags.push({
      operator: SearchEntityTagOperatorEnum.GT,
      tag: UserSearchEntityTagNameEnum.VERIFICATION_PUBLISHED_AT,
      value: joinedFrom,
    });
  }

  if (joinedTo) {
    verificationPublishedAtSearchTags.push({
      operator: SearchEntityTagOperatorEnum.LT,
      tag: UserSearchEntityTagNameEnum.VERIFICATION_PUBLISHED_AT,
      value: joinedTo,
    });
  }

  return {
    searchTagsWithAndCondition: [
      ...searchPatternSearchTags,
      ...placeSearchTags,
      ...relocationSearchTags,
      ...remoteSearchTags,
      ...salaryRangeSearchTags,
      ...verificationPublishedAtSearchTags,
    ],
    searchTagsWithOrCondition: [...talentsSearchTags, ...senioritySearchTags, ...specialitySearchTags],
  };
};
